import { useState, useEffect, useCallback, useRef } from 'react';
import { SearchFilters } from '../types';
import { fetchJobsFromRemote } from '../lib/supabase';
import { logApiCall, logError, logInfo, startTimer } from '../lib/logger';

export interface UseJobsOptions {
  initialLimit?: number;
  filter: SearchFilters;
}

export default function useJobsFromRemote({
  initialLimit = 1000,
  filter,  
}: UseJobsOptions) {
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState<number | null>(null);
  const [cursorId, setCursorId] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [nextCursorId, setNextCursorId] = useState<number | null>(null);
  const limit = useRef(initialLimit);

  // Store current filters in a ref so loadMore can access them
  const currentFiltersRef = useRef<SearchFilters>(filter);

  // Stable fetch function
  const fetchData = useCallback(async (
    searchFilters: SearchFilters,
    fetchCursor: number | null,
    fetchCursorId: number | null,
    fetchLimit: number,
    abortSignal?: AbortSignal
  ) => { 
    const endTimer = startTimer('fetchJobsFromRemote');
    setLoading(true);
    setError(null);
    
    // Update current filters ref
    currentFiltersRef.current = searchFilters;
    
    try {
      const data = await fetchJobsFromRemote({
        limit: fetchLimit,
        cursor: fetchCursor,
        cursorId: fetchCursorId,
        employerName: searchFilters.companyName,
        position: searchFilters.jobTitle,
        city: searchFilters.location,
        year: searchFilters.year,
      });
      
      if (!abortSignal?.aborted) {
        const duration = endTimer();
        logApiCall('useJobsFromRemote.fetchJobsFromRemote', 'GET', duration, 200, {
          limit: fetchLimit,
          cursor: fetchCursor,
          cursorId: fetchCursorId,
          employerName: searchFilters.companyName,
          position: searchFilters.jobTitle,
          city: searchFilters.location,
          year: searchFilters.year,
          resultCount: data.length,
          isLoadMore: fetchCursor !== null,
        });
        
        setJobs(prev => fetchCursor === null ? data : [...prev, ...data]);
        setHasMore(data.length === fetchLimit);
        
        // Update cursor for next page
        if (data.length > 0) {
          const lastItem = data[data.length - 1];
          setNextCursor(lastItem.wage_rate_of_pay_from);
          setNextCursorId(lastItem.id);
        }
      }
    } catch (err) {
      logError(err as Error, {
        function: 'fetchJobsFromRemote',
        limit: fetchLimit,
        cursor: fetchCursor,
        cursorId: fetchCursorId,
        employerName: searchFilters.companyName,
        position: searchFilters.jobTitle,
        city: searchFilters.location,
        year: searchFilters.year,
      });
      setError(err as Error);
    } finally {
      if (!abortSignal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  // Effect to handle cursor changes (for load more functionality)
  useEffect(() => {
    if (cursor !== null && currentFiltersRef.current) {
      // This is a load more operation
      fetchData(
        currentFiltersRef.current,
        cursor,
        cursorId,
        limit.current
      );
    }
  }, [cursor, cursorId, fetchData]);

  // Function to load more jobs (pagination)
  const loadMore = useCallback(() => {
    if (hasMore && !loading && nextCursor !== null) {
      setCursor(nextCursor);
      setCursorId(nextCursorId);
    }
  }, [hasMore, loading, nextCursor, nextCursorId]);

  // Function to reset search with new filters
  const resetSearch = useCallback((jobOptions: Partial<UseJobsOptions> = {}) => {
    logInfo("Reset search called", jobOptions);
    
    // Reset state
    setCursor(null);
    setCursorId(null);
    setNextCursor(null);
    setNextCursorId(null);
    setJobs([]);
    setHasMore(true);
    setError(null);
    setLoading(false);
  }, []);

  return {
    jobs,
    loading,
    error,
    hasMore,
    loadMore,
    resetSearch,
    fetchData
  };
} 